import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";

/**
 * Compact 2-month availability calendar.
 * Fetches blocked ranges from /api/vehicles/{id}/availability and renders
 * a side-by-side month view with red-shaded blocked days.
 * Optionally highlights the user's currently selected start/end range in green.
 */
const pad = (n) => String(n).padStart(2, "0");
const dateKey = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const parseISO = (s) => {
  if (!s) return null;
  const d = new Date(s.length === 10 ? `${s}T00:00:00` : s);
  return isNaN(d.getTime()) ? null : d;
};

const buildMonthDays = (year, month) => {
  const first = new Date(year, month, 1);
  const last = new Date(year, month + 1, 0);
  // Monday-first grid
  const startOffset = (first.getDay() + 6) % 7;
  const cells = [];
  for (let i = 0; i < startOffset; i++) cells.push(null);
  for (let d = 1; d <= last.getDate(); d++) cells.push(new Date(year, month, d));
  return cells;
};

const MONTH_NAMES_DE = [
  "Januar", "Februar", "März", "April", "Mai", "Juni",
  "Juli", "August", "September", "Oktober", "November", "Dezember",
];
const WEEKDAY_SHORT = ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"];

const Month = ({ year, month, blockedSet, selectedStart, selectedEnd, onPick }) => {
  const cells = useMemo(() => buildMonthDays(year, month), [year, month]);
  const today = new Date(); today.setHours(0, 0, 0, 0);
  return (
    <div className="bg-white border border-[var(--rs-border)] p-3 rounded-xl">
      <div className="text-xs font-bold uppercase tracking-[0.15em] text-[var(--rs-primary)] mb-2">
        {MONTH_NAMES_DE[month]} {year}
      </div>
      <div className="grid grid-cols-7 gap-1 text-[10px] text-[var(--rs-text-muted)] mb-1">
        {WEEKDAY_SHORT.map((d) => (<div key={d} className="text-center font-bold">{d}</div>))}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {cells.map((d, i) => {
          if (!d) return <div key={`e${i}`} className="aspect-square" />;
          const k = dateKey(d);
          const isPast = d < today;
          const isBlocked = blockedSet.has(k);
          const inRange = selectedStart && selectedEnd && d >= selectedStart && d <= selectedEnd;
          const isPickStart = selectedStart && dateKey(selectedStart) === k;
          const isPickEnd = selectedEnd && dateKey(selectedEnd) === k;
          const cls = [
            "aspect-square text-[11px] flex items-center justify-center rounded-md transition-colors",
          ];
          if (isPast) cls.push("text-[var(--rs-text-muted)]/40 cursor-not-allowed");
          else if (isBlocked) cls.push("bg-red-100 text-red-700 line-through cursor-not-allowed");
          else if (isPickStart || isPickEnd) cls.push("bg-[var(--rs-primary)] text-white font-bold cursor-pointer");
          else if (inRange) cls.push("bg-[var(--rs-primary)]/15 text-[var(--rs-primary)] font-bold cursor-pointer");
          else cls.push("hover:bg-[var(--rs-surface-2)] cursor-pointer");
          return (
            <button
              key={k}
              type="button"
              disabled={isPast || isBlocked}
              onClick={() => onPick && onPick(k)}
              className={cls.join(" ")}
              data-testid={`avail-day-${k}`}
            >
              {d.getDate()}
            </button>
          );
        })}
      </div>
    </div>
  );
};

const AvailabilityCalendar = ({ vehicleId, startDate, endDate, onPickStart, onPickEnd }) => {
  const [ranges, setRanges] = useState([]);
  const [loading, setLoading] = useState(false);
  const [offset, setOffset] = useState(0); // 0 = current month + next, 2 = +2 months etc.

  useEffect(() => {
    if (!vehicleId) return;
    let cancelled = false;
    setLoading(true);
    api.get(`/vehicles/${vehicleId}/availability`, { params: { days: 120 } })
      .then((r) => { if (!cancelled) setRanges(r.data?.ranges || []); })
      .catch(() => { if (!cancelled) setRanges([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [vehicleId]);

  // Expand ranges into per-day Set
  const blockedSet = useMemo(() => {
    const s = new Set();
    for (const r of ranges) {
      const a = parseISO(r.start);
      const b = parseISO(r.end);
      if (!a || !b) continue;
      const cursor = new Date(a.getFullYear(), a.getMonth(), a.getDate());
      const endDay = new Date(b.getFullYear(), b.getMonth(), b.getDate());
      while (cursor <= endDay) {
        s.add(dateKey(cursor));
        cursor.setDate(cursor.getDate() + 1);
      }
    }
    return s;
  }, [ranges]);

  const today = new Date();
  const baseMonth = today.getMonth() + offset;
  const m1 = new Date(today.getFullYear(), baseMonth, 1);
  const m2 = new Date(today.getFullYear(), baseMonth + 1, 1);

  const selStart = parseISO(startDate);
  const selEnd = parseISO(endDate);

  // Click logic: if no start OR clicked day < start → set start; otherwise set end
  const handlePick = (k) => {
    const clicked = parseISO(k);
    if (!selStart || (selEnd && selStart && clicked < selStart) || (selStart && selEnd)) {
      // restart selection
      onPickStart && onPickStart(k);
      onPickEnd && onPickEnd(k);
    } else if (selStart && !selEnd) {
      if (clicked < selStart) onPickStart && onPickStart(k);
      else onPickEnd && onPickEnd(k);
    } else {
      onPickStart && onPickStart(k);
    }
  };

  return (
    <div className="rounded-xl border border-[var(--rs-border)] bg-[var(--rs-surface)] p-4" data-testid="availability-calendar">
      <div className="flex items-center justify-between mb-3">
        <div className="text-xs font-bold uppercase tracking-[0.18em] text-[var(--rs-text-muted)]">
          Verfügbarkeit
        </div>
        <div className="flex items-center gap-1">
          <button type="button" disabled={offset <= 0}
            onClick={() => setOffset((o) => Math.max(0, o - 2))}
            className="px-2 py-1 text-xs border border-[var(--rs-border)] rounded-md hover:bg-white disabled:opacity-40"
            data-testid="avail-prev">←</button>
          <button type="button" disabled={offset >= 4}
            onClick={() => setOffset((o) => Math.min(4, o + 2))}
            className="px-2 py-1 text-xs border border-[var(--rs-border)] rounded-md hover:bg-white disabled:opacity-40"
            data-testid="avail-next">→</button>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Month year={m1.getFullYear()} month={m1.getMonth()} blockedSet={blockedSet}
          selectedStart={selStart} selectedEnd={selEnd} onPick={handlePick} />
        <Month year={m2.getFullYear()} month={m2.getMonth()} blockedSet={blockedSet}
          selectedStart={selStart} selectedEnd={selEnd} onPick={handlePick} />
      </div>
      <div className="flex items-center gap-4 mt-3 text-[10px] text-[var(--rs-text-muted)]">
        <span className="flex items-center gap-1.5">
          <span className="inline-block w-3 h-3 rounded-sm bg-red-100 border border-red-200"></span> Belegt
        </span>
        <span className="flex items-center gap-1.5">
          <span className="inline-block w-3 h-3 rounded-sm bg-[var(--rs-primary)]"></span> Auswahl
        </span>
        <span className="flex items-center gap-1.5">
          <span className="inline-block w-3 h-3 rounded-sm bg-[var(--rs-primary)]/15 border border-[var(--rs-primary)]/30"></span> Im Zeitraum
        </span>
        {loading && <span className="ml-auto">Lade…</span>}
      </div>
    </div>
  );
};

export default AvailabilityCalendar;
